// @section 1 AccountFindingsLens — discovered research items
//
// Rows from hq_bd.findings, FK'd to a specific deal. Each finding has a
// category (hubspot / GA4 / Google Ads / website perf / SEO / enrichment /
// org structure / financial / other), an optional severity, and a status
// workflow (to-research → in-progress → done; deferred / dropped exit states).
//
// Promoted to a top-level tab on 2026-05-27 — used to be a sub-tab inside
// the retired BD lens.

import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import {
  listBdFindings, createBdFinding, patchBdFinding,
  type AccountAggregate, type AccountDeal, type BdFinding,
} from '../../api';

export default function AccountFindingsLens({ data }: { data: AccountAggregate }) {
  const companyId = data.company.id;
  const deals = data.deals;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {deals.length === 0 && <NoDealsBanner />}
      <FindingsSection companyId={companyId} deals={deals} />
    </div>
  );
}

function FindingsSection({ companyId, deals }: { companyId: string; deals: AccountDeal[] }) {
  const [items, setItems] = useState<BdFinding[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = () => listBdFindings(companyId).then(setItems).catch((e) => setErr(String(e)));
  useEffect(() => { reload(); }, [companyId]);

  const openCount = items?.filter((f) => f.status === 'to-research' || f.status === 'in-progress').length ?? 0;

  return (
    <Card title="Findings" subtitle={items ? `${openCount} active · ${items.length} total` : 'Loading…'} err={err}>
      {items !== null && (
        <>
          {items.length === 0 ? (
            <Empty>No findings logged. Capture research items, audit notes, anything you've discovered — done or to-research.</Empty>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {items.map((f) => <FindingRow key={f.id} finding={f} onChanged={reload} />)}
            </ul>
          )}
          {deals.length > 0 && <FindingForm companyId={companyId} deals={deals} onCreated={reload} />}
        </>
      )}
    </Card>
  );
}

function FindingRow({ finding, onChanged }: { finding: BdFinding; onChanged: () => void }) {
  async function setStatus(status: string) {
    await patchBdFinding(finding.id, { status });
    onChanged();
  }
  return (
    <li style={rowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13 }}>
          <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, color: '#6b7a90', marginRight: 8 }}>{finding.category}</span>
          {finding.severity && <SeverityChip s={finding.severity} />}
          <strong>{finding.title}</strong>
        </div>
        {finding.body && <div style={{ fontSize: 12, color: '#a3b1c2', marginTop: 4, whiteSpace: 'pre-wrap' }}>{finding.body}</div>}
        {(finding.sourceSystem || finding.sourceUrl) && (
          <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 4 }}>
            source: {finding.sourceSystem ?? '—'}
            {finding.sourceUrl && <> · <a href={finding.sourceUrl} target="_blank" rel="noreferrer">open</a></>}
          </div>
        )}
        {finding.dueAt && <div style={{ fontSize: 11, color: '#6b7a90' }}>due {new Date(finding.dueAt).toLocaleDateString()}</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        <StatusBadge status={finding.status} />
        {finding.status !== 'done' && finding.status !== 'dropped' && (
          <button onClick={() => setStatus('done')} style={btnSm}>Mark done</button>
        )}
        {finding.status === 'to-research' && (
          <button onClick={() => setStatus('in-progress')} style={btnSm}>Start</button>
        )}
        {finding.status === 'done' && (
          <button onClick={() => setStatus('to-research')} style={btnSm}>Reopen</button>
        )}
      </div>
    </li>
  );
}

function FindingForm({ companyId, deals, onCreated }: { companyId: string; deals: AccountDeal[]; onCreated: () => void }) {
  const [dealId, setDealId] = useState(deals[0].id);
  const [category, setCategory] = useState('other');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<'info' | 'low' | 'medium' | 'high' | 'critical' | ''>('');
  const [status, setStatus] = useState<'done' | 'to-research' | 'in-progress'>('done');
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={{ ...formStyle, flexWrap: 'wrap' }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setBusy(true);
        try {
          await createBdFinding({
            companyId, dealId,
            category, title: title.trim(),
            body: body.trim() || undefined,
            severity: severity || undefined,
            status,
          });
          setTitle(''); setBody(''); setSeverity('');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <DealPicker deals={deals} value={dealId} onChange={setDealId} />
      <select value={category} onChange={(e) => setCategory(e.target.value)}>
        <option value="hubspot">HubSpot</option>
        <option value="google-analytics">GA4</option>
        <option value="google-ads">Google Ads</option>
        <option value="website-perf">Website perf</option>
        <option value="seo">SEO</option>
        <option value="prospect-enrichment">Enrichment</option>
        <option value="org-structure">Org structure</option>
        <option value="financial">Financial</option>
        <option value="other">Other</option>
      </select>
      <input placeholder="title" value={title} onChange={(e) => setTitle(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
      <select value={severity} onChange={(e) => setSeverity(e.target.value as typeof severity)}>
        <option value="">Severity…</option>
        <option value="info">Info</option>
        <option value="low">Low</option>
        <option value="medium">Medium</option>
        <option value="high">High</option>
        <option value="critical">Critical</option>
      </select>
      <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
        <option value="done">Done (logging)</option>
        <option value="to-research">To research</option>
        <option value="in-progress">In progress</option>
      </select>
      <input placeholder="body (optional)" value={body} onChange={(e) => setBody(e.target.value)} style={{ flex: 1, minWidth: 240 }} />
      <button type="submit" disabled={busy || !title.trim()}>Add</button>
    </form>
  );
}

// @section 2 Shared local helpers
function NoDealsBanner() {
  return (
    <div style={{ ...cardStyle, borderStyle: 'dashed' }}>
      <div style={{ fontSize: 13, color: '#a3b1c2' }}>
        This account has no deals yet. Findings attach to a specific deal — create one in the Overview lens first.
      </div>
    </div>
  );
}

function Card({ title, subtitle, err, children }: { title: string; subtitle?: string; err?: string | null; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a3b1c2' }}>{title}</h3>
        {subtitle && <span style={{ fontSize: 11, color: '#6b7a90' }}>{subtitle}</span>}
      </div>
      {err && <div style={{ color: '#e87e7e', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <em style={{ color: '#6b7a90', fontSize: 13 }}>{children}</em>;
}

function DealPicker({ deals, value, onChange }: { deals: AccountDeal[]; value: string; onChange: (v: string) => void }) {
  if (deals.length === 1) {
    return <span style={{ fontSize: 11, color: '#6b7a90' }}>deal: {deals[0].name}</span>;
  }
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {deals.map((d) => (
        <option key={d.id} value={d.id}>{d.name}{d.closedAt ? ' (closed)' : ''}</option>
      ))}
    </select>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === 'to-research' ? '#7eb6e8'
    : status === 'in-progress' ? '#f0c674'
    : status === 'done' ? '#6ec78a'
    : status === 'dropped' ? '#6b7a90'
    : status === 'deferred' ? '#a08fd8'
    : '#a3b1c2';
  return (
    <span style={{
      fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600,
      padding: '2px 8px', borderRadius: 4,
      background: `${tone}22`, color: tone,
    }}>{status}</span>
  );
}

function SeverityChip({ s }: { s: string }) {
  const tone = s === 'critical' ? '#e87e7e' : s === 'high' ? '#f0a96b' : s === 'medium' ? '#f0c674' : '#7eb6e8';
  return (
    <span style={{
      fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700,
      padding: '1px 6px', borderRadius: 3, marginRight: 8,
      background: `${tone}22`, color: tone,
    }}>{s}</span>
  );
}

const cardStyle: CSSProperties = {
  border: '1px solid #232a37',
  borderRadius: 8,
  padding: 14,
  background: '#171c26',
};
const rowStyle: CSSProperties = {
  padding: '8px 0',
  borderTop: '1px solid #232a37',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
};
const formStyle: CSSProperties = {
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px dashed #232a37',
  display: 'flex',
  gap: 8,
  alignItems: 'center',
};
const btnSm: CSSProperties = {
  fontSize: 11,
  padding: '3px 8px',
  border: '1px solid #2a3346',
  borderRadius: 4,
  background: 'transparent',
  color: '#a3b1c2',
  cursor: 'pointer',
};
